import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import type { LeaderGroupForm } from '../types'
import { Table } from '@/components_deprecated/Table'
import { useMemo, useState } from 'react'
import { Icon } from '@ur-solutions/react-components'
import { Input, UserSelect } from '@/components_deprecated'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import { SalaryGroupSelect } from '@/components_deprecated/Select/SalaryGroupSelect'
import { ModuleCombobox, PromptModal } from '@/components'
import type { Module } from '@/modules/companies'

interface EditLeaderGroupPromptProps {
  initialData?: LeaderGroupForm
  includedModuleNames?: Module[]
  companyUsers: {
    id: string
    fullName: string
  }[]
  onSubmit: (data: LeaderGroupForm | null) => void
}

export const EditLeaderGroupPrompt: React.FC<EditLeaderGroupPromptProps> = ({
  initialData,
  includedModuleNames,
  companyUsers,
  onSubmit,
}) => {
  const { formValues, formChangeHandler, formValid } = useForm<LeaderGroupForm>(
    {
      values: initialData ?? {
        name: '',
        leaders: [],
        users: [],
        salaryGroup: null,
        includedModules: [],
      },
      config: {
        initAsInvalid: true,
      },
      validators: {
        name: () => {
          if (!formValues.name) {
            return translations.error.required
          }
          return null
        },
      },
    }
  )

  const [userSearch, setUserSearch] = useState('')

  const translations = useTranslate({
    label: {
      name: 'common.name',
      remove: 'common.remove',
      search: 'common.search',
      addUsers: 'common.add-users',
      responsibleUsers: 'common.responsible-users',
      salaryGroup: 'salary.salary-group',
      modules: 'common.modules',
    },
    create: 'common.create-alt',
    edit: 'common.edit',
    update: 'common.update',
    users: 'common.users',

    error: {
      required: 'common.required',
    },
  })

  const selectedUsers = useMemo(
    () =>
      companyUsers.filter(
        user =>
          formValues.users.includes(user.id) &&
          user.fullName.includes(userSearch)
      ),
    [companyUsers, formValues.users, userSearch]
  )

  return (
    <PromptModal
      title={initialData ? translations.edit : translations.create}
      submitText={translations.update}
      submitDisabled={!formValid}
      cancelOnClickOutside
      onCancel={() => onSubmit(null)}
      onSubmit={() => onSubmit(formValues)}
      className="w-[600px]"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={event => {
          event.preventDefault()
          onSubmit(formValues)
        }}
      >
        <label className="flex flex-col items-start">
          <span className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
            {translations.label.name}
          </span>
          <Input
            value={formValues.name}
            onChange={formChangeHandler('name')}
            grayed
          />
        </label>
        <label className="flex flex-col items-start">
          <span className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
            {translations.label.responsibleUsers}
          </span>
          <SelectMultipleUsers
            selected={formValues.leaders}
            onChange={formChangeHandler('leaders')}
            grayed
          />
        </label>
        <label className="flex flex-col items-start">
          <span className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
            {translations.label.salaryGroup}
          </span>
          <SalaryGroupSelect
            value={formValues.salaryGroup}
            onChange={formChangeHandler('salaryGroup')}
            grayed
            nullable
          />
        </label>
        <div className="flex flex-col items-start">
          <span className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
            {translations.label.modules}
          </span>
          <ModuleCombobox
            mode="multiple"
            value={formValues.includedModules}
            include={includedModuleNames}
            onChange={formChangeHandler('includedModules')}
            PopoverContentProps={{
              onMouseUp: e => e.nativeEvent.stopImmediatePropagation(), // Prevent popover from closing when clicking on options
            }}
          />
        </div>
        <div className="flex w-full gap-4">
          <label className="flex flex-1 flex-col items-start">
            <span className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
              {translations.label.addUsers}
            </span>
            <UserSelect
              value={null}
              exclude={selectedUsers.map(user => user.id)}
              onChange={val =>
                val
                  ? formChangeHandler('users')(
                      formValues.users.includes(val)
                        ? formValues.users.filter(id => id !== val)
                        : [...formValues.users, val]
                    )
                  : void 0
              }
              grayed
            />
          </label>

          <label className="flex flex-1 flex-col items-start">
            <span className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
              {translations.label.search}
            </span>
            <Input
              value={userSearch}
              onChange={val => setUserSearch(val)}
              grayed
              placeholder={translations.label.search}
            />
          </label>
        </div>

        <Table headerMargin noData={formValues.users.length === 0}>
          <thead>
            <tr>
              <th>{translations.label.name}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {selectedUsers.map(user => (
              <tr key={user.id}>
                <td>{user.fullName}</td>
                <td width="1px">
                  <Icon
                    icon="times"
                    cursor="pointer"
                    color="gray6"
                    hoverColor="red"
                    onClick={() =>
                      formChangeHandler('users')(
                        formValues.users.filter(id => id !== user.id)
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </form>
    </PromptModal>
  )
}
